import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import { useScanInput } from '../hooks/useScanInput';
import { useAudioFeedback } from '../hooks/useAudioFeedback';
import { useToast } from '../components/ui/Toast';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { PageHeader } from '../components/ui/PageHeader';

/** The server's cascade (EAN → supplier ref → OEM → text) unless the operator pins one mode. */
type ScanMode = 'AUTO' | 'EAN' | 'SUPPLIER_REF' | 'MANUFACTURER_REF' | 'MANUAL';
type Condition = 'OK' | 'DAMAGED' | 'MISSING';
type Confidence = 'GREEN' | 'AMBER' | 'RED';

/** SC-02 step 7 — what the delivery note's reference resolves to. */
interface ReceptionMatch {
  supplierOrder: {
    id: string;
    poNumber: string;
    status: string;
    supplierOrderRef: string | null;
    supplierName: string;
  };
  expectedLines: Array<{
    supplierOrderLineId: string;
    productId: string;
    orderNumber: string | null;
    /** Customer-order parts go straight into the order's bin (order number when none typed). */
    orderBin?: string | null;
    destination: 'CUSTOMER_ORDER' | 'STOCK';
  }>;
  linkedCustomerOrders: Array<{
    orderId: string;
    orderNumber: string;
    status: string;
    customerName: string | null;
    lineCount: number;
  }>;
}

/** SC-02 step 8 — one identified item, as the resolver answers. */
interface ScanCandidate {
  productId: string;
  sku: string;
  name: string;
  brand: string | null;
  supplierOrderLineId: string | null;
  expectedQty: number | null;
  alreadyReceived: number | null;
}
interface ScanResult {
  mode: Exclude<ScanMode, 'AUTO'> | 'QUICK_CREATE';
  confidence: Confidence;
  autoValidated: boolean;
  candidates: ScanCandidate[];
  scanId: string;
  productId: string | null;
}

interface PoSummary {
  id: string;
  poNumber: string;
  status: string;
  supplierName: string;
  supplierOrderRef: string | null;
  lineCount: number;
  qtyOrdered: number;
  qtyReceived: number;
  // WP-4 send state: a PO still PENDING never reached the supplier (API down) and is retrying.
  autoCreated: boolean;
  sendAttempts: number;
  lastSendError: string | null;
  nextRetryAt: string | null;
  escalatedAt: string | null;
  closedShort: boolean;
  brfNumber: string | null;
}

interface POLine {
  id: string;
  productId: string;
  quantity: number;
  quantityReceived: number;
  received: boolean;
  // WP-4: what the customer actually needs (the rest is MOQ / pack surplus for stock), what
  // never came (§8c short-ship), and whether a too-late cancellation released the customer link.
  quantityCustomer: number | null;
  quantityShort: number;
  customerReleased: boolean;
  productSku: string | null;
  productName: string | null;
  productBrand: string | null;
  ean: string | null;
  manufacturerRef: string | null;
}

interface PoDetail extends Omit<PoSummary, 'lineCount' | 'qtyOrdered' | 'qtyReceived'> {
  lines: POLine[];
}

/**
 * One line of the reception's audit trail — J2.5, "scan_mode is recorded in the reception log for
 * audit". `actorName` is WHO scanned it: reception_scans carries actor_id and the endpoint has
 * always joined users for the name, but the screen only ever read `log.length` and threw every
 * row away, so a reception could be questioned later with no way to say who had counted it.
 */
interface ScanLogRow {
  id: string;
  scanInput: string;
  scanMode: string;
  confidence: Confidence;
  quantity: number;
  condition: Condition;
  createdAt: string;
  productSku: string | null;
  productName: string | null;
  actorName: string | null;
}

/**
 * The PO-KEYED reception — SC-02 steps 7 → 9, the supplier reception as the spec writes it.
 *
 * Kept beside the one-pass day (below), not replaced by it: this is the BUYER's per-purchase-order
 * close, and it owns §8c short-ship and the credit release that goes with it. The floor's own
 * day, which is what guide E6 describes, is `OnePass`.
 *
 *   7. RAPPROCHEMENT — scan (or type) the reference printed on the supplier's delivery note.
 *      GET /warehouse/receptions?ref= answers with the PO, its expected lines and the CUSTOMER
 *      ORDERS waiting on them. Identification only (Invariant #40): nothing is allocated here,
 *      the screen just tells the operator whose parts are on the pallet. The PO dropdown stays
 *      as the fallback for an operator holding our own printout and no scanner.
 *   8. QUANTITATIVE VERIFICATION — every physical item is scanned and identified SERVER-SIDE
 *      (POST /warehouse/scan-reception): EAN 🟢 → supplier ref 🟡 → manufacturer/OEM ref 🟡 →
 *      free text 🔴. Ambiguity is shown, never guessed; a part the PO does not expect is said
 *      so; every scan is written to the audit trail with how it was recognised. The page used
 *      to compare the barcode against the loaded lines in the browser, which knew nothing of
 *      supplier article codes or OEM lists and had no trail.
 *   9. VALIDATE — POST /warehouse/receptions: one atomic goods-in (ledger, auto-reservation of
 *      the customer lines, §8c short-ship close), damaged units into the Zone Q register, the
 *      over/under-delivery report to the purchaser — then STEP 2, shelving, one click away.
 *
 * Step 8's "create the product on the fly" is deliberately absent: an unknown item is, by
 * definition, not on the purchase order and there is no line to receive it against — it is an
 * over-delivery for the purchaser, not a reception.
 */
export function ByPurchaseOrder() {
  const { t } = useTranslation();
  const beep = useAudioFeedback();
  const { showApiError, push } = useToast();
  const navigate = useNavigate();
  const [pos, setPos] = useState<PoSummary[]>([]);
  const [po, setPo] = useState<PoDetail | null>(null);
  /** Step 7's answer for the open PO — destinations per line, customer orders on the pallet. */
  const [match, setMatch] = useState<ReceptionMatch | null>(null);
  const [refInput, setRefInput] = useState('');
  const [mode, setMode] = useState<ScanMode>('AUTO');
  const [busy, setBusy] = useState(false);
  const [counts, setCounts] = useState<Record<string, { qty: number; condition: Condition }>>({});
  /** How each line's units were identified — the 🟢🟡🔴 legend beside the count. */
  const [identified, setIdentified] = useState<Record<string, Confidence>>({});
  /** A scan that named several parts: the operator picks, and the pick is recorded. */
  const [choice, setChoice] = useState<{ input: string; candidates: ScanCandidate[] } | null>(null);
  /** A scan that named nothing: the ways left to try, and a text search box. */
  const [miss, setMiss] = useState<{ input: string; tried: string; next: ScanMode[] } | null>(null);
  const [manualQuery, setManualQuery] = useState('');
  /** The reception's audit trail, rows and all — who scanned what, when (J2.5). */
  const [scanLog, setScanLog] = useState<ScanLogRow[]>([]);
  const scanCount = scanLog.length;

  /* Re-read the trail rather than counting locally. A scan can land as several rows (or none,
     when it identified nothing), and the row carries the operator's name and the mode the server
     actually resolved — neither of which a client-side counter can invent. */
  const refreshScanLog = async (poId: string) => {
    const { data } = await apiClient
      .get<ScanLogRow[]>(`/warehouse/receptions/${poId}/scans`)
      .catch(() => ({ data: [] as ScanLogRow[] }));
    setScanLog(data);
  };

  // Inbound purchase orders (PENDING/CONFIRMED/SHIPPED) — today's goods-in work.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get<PoSummary[]>('/purchasing/orders');
        setPos(data);
      } catch (err) {
        showApiError(err);
      }
    })();
  }, [showApiError]);

  const openPo = async (id: string) => {
    if (!id) {
      setPo(null);
      setMatch(null);
      setCounts({});
      setIdentified({});
      setChoice(null);
      setMiss(null);
      setScanLog([]);
      return;
    }
    try {
      const { data } = await apiClient.get<PoDetail>(`/purchasing/orders/${id}`);
      setPo(data);
      // Start every line at 0 received this session; the operator scans or types the count.
      setCounts(
        Object.fromEntries(data.lines.map((l) => [l.id, { qty: 0, condition: 'OK' as Condition }]))
      );
      setIdentified({});
      setChoice(null);
      setMiss(null);
      // Step 7 for a PO picked from the list: the same rapprochement, keyed by our PO number,
      // so the destinations and the customer orders show whichever way the PO was opened.
      try {
        const { data: m } = await apiClient.get<ReceptionMatch>(
          `/warehouse/receptions?ref=${encodeURIComponent(data.poNumber)}`
        );
        setMatch(m);
      } catch {
        setMatch(null);
      }
      const { data: log } = await apiClient
        .get<ScanLogRow[]>(`/warehouse/receptions/${id}/scans`)
        .catch(() => ({ data: [] as ScanLogRow[] }));
      setScanLog(log);
    } catch (err) {
      showApiError(err);
    }
  };

  /**
   * SC-02 step 7 — the reference off the supplier's delivery note (or our own PO number).
   * Resolves to ONE purchase order; the screen then opens it exactly as the dropdown would.
   */
  const findReception = async (ref: string) => {
    const needle = ref.trim();
    if (!needle) return;
    setBusy(true);
    try {
      const { data } = await apiClient.get<ReceptionMatch>(
        `/warehouse/receptions?ref=${encodeURIComponent(needle)}`
      );
      beep.success();
      await openPo(data.supplierOrder.id);
      setRefInput('');
    } catch (err) {
      beep.wrongProduct();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((err as any)?.response?.status === 404) {
        push({ tone: 'warning', message: t('reception.noPoForRef', { ref: needle }) });
      } else {
        showApiError(err);
      }
    } finally {
      setBusy(false);
    }
  };

  /** Which of this PO's lines a candidate lands on, and what it is for. */
  const destinationOf = (lineId: string) =>
    match?.expectedLines.find((l) => l.supplierOrderLineId === lineId);

  const bump = (lineId: string, by: number) =>
    setCounts((c) => ({
      ...c,
      [lineId]: {
        qty: Math.max(0, (c[lineId]?.qty ?? 0) + by),
        condition: c[lineId]?.condition ?? 'OK',
      },
    }));

  /**
   * One identified product → one PO line to count it on. The same part can sit on two lines
   * (2 for a customer, 3 for stock — the ordinary SC-02 shape); the customer's line is filled
   * first, because those units are the ones someone is waiting for, and a line already counted
   * up to what was ordered yields to the next. Which line the units are BOOKED against is still
   * decided at validation, from the counts — this only decides where the count goes.
   */
  const lineFor = (productId: string, cands: ScanCandidate[]): POLine | undefined => {
    if (!po) return undefined;
    const ids = new Set(
      cands
        .filter((c) => c.productId === productId && c.supplierOrderLineId)
        .map((c) => c.supplierOrderLineId!)
    );
    const lines = po.lines.filter((l) => ids.has(l.id));
    if (lines.length === 0) return undefined;
    const rank = (l: POLine) => (destinationOf(l.id)?.destination === 'CUSTOMER_ORDER' ? 0 : 1);
    const open = (l: POLine) => (counts[l.id]?.qty ?? 0) + l.quantityReceived < l.quantity;
    return (
      [...lines].sort((x, y) => rank(x) - rank(y)).find(open) ??
      [...lines].sort((x, y) => rank(x) - rank(y))[0]
    );
  };

  /** Count one unit of an identified part, or say why it cannot be counted here. */
  const accept = (productId: string, cands: ScanCandidate[], confidence: Confidence) => {
    const line = lineFor(productId, cands);
    if (!line) {
      beep.wrongProduct();
      const c = cands.find((x) => x.productId === productId);
      push({
        tone: 'warning',
        title: t('reception.notOnOrder'),
        message: t('reception.notOnThisPo', { sku: c?.sku ?? productId, po: po?.poNumber ?? '' }),
      });
      return;
    }
    beep.success();
    bump(line.id, 1);
    setIdentified((cur) => ({ ...cur, [line.id]: confidence }));
    setChoice(null);
    setMiss(null);
  };

  /**
   * SC-02 step 8 — identify one physical item. The resolver runs the cascade server-side and
   * records the scan; here we only decide what its answer means for the count.
   */
  const scanItem = async (text: string, force?: Exclude<ScanMode, 'AUTO'>, pickedId?: string) => {
    if (!po) return;
    const explicit = force ?? (mode === 'AUTO' ? undefined : mode);
    try {
      const { data } = await apiClient.post<ScanResult>('/warehouse/scan-reception', {
        supplierOrderId: po.id,
        scanInput: text,
        ...(explicit ? { scanMode: explicit } : {}),
        ...(pickedId ? { productId: pickedId } : {}),
        qty: 1,
      });
      const distinct = [...new Set(data.candidates.map((c) => c.productId))];
      // Every scan is in the trail, including the ones that identified nothing.
      if (!pickedId) void refreshScanLog(po.id);
      if (pickedId) {
        accept(pickedId, data.candidates, data.confidence);
        return;
      }
      if (distinct.length === 1) {
        accept(distinct[0], data.candidates, data.confidence);
        return;
      }
      if (distinct.length > 1) {
        // Ambiguity is a property of the part, and it is the operator's to resolve — by eye.
        beep.error();
        setMiss(null);
        setChoice({ input: text, candidates: data.candidates });
        return;
      }
      // Nothing, anywhere the cascade looked. Offer what is left to try.
      beep.wrongProduct();
      const order: Exclude<ScanMode, 'AUTO'>[] = [
        'EAN',
        'SUPPLIER_REF',
        'MANUFACTURER_REF',
        'MANUAL',
      ];
      const next = explicit ? order.slice(order.indexOf(explicit) + 1) : [];
      setChoice(null);
      setMiss({ input: text, tried: explicit ?? 'AUTO', next });
    } catch (err) {
      beep.error();
      showApiError(err);
    }
  };

  useScanInput({
    onScan: (text) => {
      // No PO open yet: the first thing scanned on a pallet is the delivery note (step 7).
      if (!po) {
        void findReception(text);
        return;
      }
      void scanItem(text);
    },
  });

  const toPost = Object.entries(counts).filter(([, v]) => v.qty > 0);

  /**
   * WP-4 — why a purchase order is not yet at the supplier. A PENDING PO means PO_SEND failed
   * (or the supplier has no API and it must be placed by hand at the cutoff): it keeps retrying
   * with backoff and escalates to the manager/owner past poRetryMax. The customer order is
   * confirmed regardless — its line simply reads "awaiting supplier".
   */
  const sendState = (
    p: Pick<PoSummary, 'status' | 'sendAttempts' | 'lastSendError' | 'nextRetryAt' | 'escalatedAt'>
  ) => {
    if (p.status !== 'PENDING') return null;
    if (p.lastSendError === 'MANUAL_CHANNEL') {
      return { tone: 'text-muted-foreground', label: t('reception.send.manual') };
    }
    if (p.escalatedAt) {
      return {
        tone: 'text-destructive font-semibold',
        label: t('reception.send.escalated', { attempts: p.sendAttempts }),
      };
    }
    if (p.sendAttempts > 0) {
      const when = p.nextRetryAt ? new Date(p.nextRetryAt).toLocaleTimeString('fr-FR') : '—';
      return {
        tone: 'text-warning',
        label: t('reception.send.retrying', { attempts: p.sendAttempts, when }),
      };
    }
    return { tone: 'text-muted-foreground', label: t('reception.send.pending') };
  };

  const validate = async () => {
    if (!po || toPost.length === 0) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post<{
        brfNumber: string;
        closedShort: boolean;
        // §8c — one entry per customer order this reception closed short.
        shortClosed?: Array<{
          orderNumber: string | null;
          orderFullyClosed: boolean;
          draft: { id: string; amountTTC: number } | null;
        }>;
      }>(
        // The floor's spelling of PO_RECEIVE: same act, same code path as the purchaser's, plus
        // the Zone Q register for damaged units and the over/under-delivery report.
        '/warehouse/receptions',
        {
          poId: po.id,
          lines: toPost.map(([id, v]) => ({
            supplierOrderLineId: id,
            qty: v.qty,
            condition: v.condition,
          })),
        },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      // §8c — say what actually happened to the CUSTOMER, because the operator standing here is
      // the last person who can still ring them. The customer line closes at the received
      // quantity and its reservation is released on the spot; the missing quantity does NOT go
      // back into sourcing for that customer — re-ordering is a commercial decision, so it
      // becomes an ordinary replenishment need and the counter raises a new order if the client
      // still wants it. A prepaid client is additionally owed money, and that avoir is only
      // PREPARED — accounting still has to issue it.
      const closed = data.shortClosed ?? [];
      const prepared = closed.filter((c) => c.draft);
      push({
        tone: data.closedShort ? 'warning' : 'success',
        message: data.closedShort
          ? t('reception.receivedShort', {
              brf: data.brfNumber,
              closed:
                closed.length > 0
                  ? t('reception.closedOrders', { count: closed.length })
                  : t('reception.closedPo'),
              prepared:
                prepared.length > 0
                  ? t('reception.preparedAvoirs', { count: prepared.length })
                  : '',
            })
          : t('reception.receivedFull', { brf: data.brfNumber }),
      });
      await openPo(po.id); // reload so quantityReceived reflects what was just booked in
      /* SC-02 step 10a — STEP 2 follows STEP 1, and the operator has to be TAKEN there. Only
         replenishment goes to the racks: a line whose quantity exceeds what the customer ordered
         (or that serves no order at all) leaves units in Zone R with nowhere to be. Customer
         lines stay in R under reservation and need nothing, so a reception made entirely of
         them gets no prompt — an empty shelving screen would only say "nothing to do". */
      const stockUnits = toPost.reduce((n, [id, v]) => {
        const line = po.lines.find((l) => l.id === id);
        if (!line || v.condition !== 'OK') return n;
        return n + Math.max(0, v.qty - (line.quantityCustomer ?? 0));
      }, 0);
      if (stockUnits > 0) {
        push({
          tone: 'info',
          message: t('reception.shelveHint'),
          action: {
            label: t('reception.shelveNow'),
            onClick: () => navigate(`/warehouse/shelving?po=${po.id}`),
          },
        });
      }
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* A tab of Reception and of the receiving tablet — the page around it carries the title. */}
      <p className="text-sm text-muted-foreground">{t('reception.intro')}</p>

      {/* SC-02 step 7 — the delivery note first. Its reference is what the supplier printed and
          what the scanner reads off the pallet; our PO number works too for the operator holding
          our own printout. */}
      <section className="border rounded p-4 bg-card space-y-2">
        <h2 className="font-semibold">{t('reception.step7')}</h2>
        <form
          className="flex flex-wrap gap-2 items-center"
          onSubmit={(e) => {
            e.preventDefault();
            void findReception(refInput);
          }}
        >
          <Input
            value={refInput}
            onChange={(e) => setRefInput(e.target.value)}
            placeholder={t('reception.refPlaceholder')}
            className="flex-1 min-w-[18rem] font-mono"
            aria-label={t('reception.deliveryNoteRef')}
            autoFocus
          />
          <button
            type="submit"
            disabled={busy || !refInput.trim()}
            className="bg-primary text-primary-foreground rounded px-4 py-2 disabled:opacity-40"
          >
            {t('reception.findReception')}
          </button>
        </form>
        <p className="text-xs text-muted-foreground">{t('reception.step7Hint')}</p>
      </section>

      <section className="border rounded p-4 bg-card space-y-2">
        <h2 className="font-semibold">{t('reception.poAndMode')}</h2>
        <div className="flex flex-wrap gap-2 items-center">
          <Select
            value={po?.id ?? ''}
            onChange={(e) => void openPo(e.target.value)}
            className="flex-1 min-w-[18rem]"
            aria-label={t('reception.purchaseOrder')}
          >
            <option value="">{t('reception.selectPo')}</option>
            {pos.map((p) => (
              <option key={p.id} value={p.id}>
                {t('reception.poOption', {
                  number: p.poNumber,
                  supplier: p.supplierName,
                  received: p.qtyReceived,
                  ordered: p.qtyOrdered,
                })}
                {sendState(p) ? ` · ${sendState(p)!.label}` : ''}
              </option>
            ))}
          </Select>
          <Select
            value={mode}
            onChange={(e) => setMode(e.target.value as ScanMode)}
            className="w-auto"
            aria-label={t('reception.scanMode')}
          >
            {(['AUTO', 'EAN', 'SUPPLIER_REF', 'MANUFACTURER_REF', 'MANUAL'] as ScanMode[]).map(
              (m) => (
                <option key={m} value={m}>
                  {t(`reception.modes.${m}`)}
                </option>
              )
            )}
          </Select>
        </div>
        {pos.length === 0 && (
          <p className="text-xs text-muted-foreground">{t('reception.noInbound')}</p>
        )}
      </section>

      {po && (
        <section className="border rounded p-4 bg-card">
          <h2 className="font-semibold mb-2">
            {po.poNumber} — {po.supplierName}
            {po.supplierOrderRef && (
              <span className="text-muted-foreground font-normal">
                {t('reception.poRef', { ref: po.supplierOrderRef })}
              </span>
            )}
            {sendState(po) && (
              <span className={`ml-2 text-xs font-normal ${sendState(po)!.tone}`}>
                ⚠ {sendState(po)!.label}
              </span>
            )}
          </h2>
          {po.status === 'PENDING' && (
            <p className="text-xs text-muted-foreground mb-2">{t('reception.notSentYet')}</p>
          )}

          {/* Step 7's answer: whose parts are on this pallet. Information only — no sorting
              happens here (Invariant #40); the reservation at validation does the allocating. */}
          {match && (
            <div className="mb-3 rounded border bg-muted/30 p-3 text-sm">
              <div className="font-medium mb-1">{t('reception.linkedOrders')}</div>
              {match.linkedCustomerOrders.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('reception.noLinkedOrders')}</p>
              ) : (
                <ul className="flex flex-wrap gap-2">
                  {match.linkedCustomerOrders.map((o) => (
                    <li key={o.orderId} className="rounded border bg-card px-2 py-1 text-xs">
                      <span className="font-mono font-medium">{o.orderNumber}</span>
                      {o.customerName && <span> · {o.customerName}</span>}
                      <span className="text-muted-foreground">
                        {' '}
                        · {t('reception.linesCount', { count: o.lineCount })} · {o.status}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Step 8 — the scanner's answer needs the operator's eyes. */}
          {choice && (
            <div className="mb-3 rounded border border-warning bg-warning/10 p-3 text-sm">
              <div className="font-medium mb-2">
                {t('reception.pickCandidate', { text: choice.input })}
              </div>
              <ul className="space-y-1">
                {[...new Map(choice.candidates.map((c) => [c.productId, c])).values()].map((c) => {
                  const onPo = choice.candidates.some(
                    (x) => x.productId === c.productId && x.supplierOrderLineId
                  );
                  return (
                    <li key={c.productId} className="flex items-center justify-between gap-2">
                      <span className="min-w-0 truncate">
                        <span className="font-mono">{c.sku}</span> · {c.name}
                        {c.brand && <span className="text-muted-foreground"> · {c.brand}</span>}
                        {!onPo && (
                          <span className="text-muted-foreground">
                            {' '}
                            — {t('reception.candidateNotOnPo')}
                          </span>
                        )}
                      </span>
                      <button
                        type="button"
                        disabled={!onPo}
                        onClick={() => void scanItem(choice.input, undefined, c.productId)}
                        className="border rounded px-3 py-1 disabled:opacity-40"
                      >
                        {t('reception.thisOne')}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <button
                type="button"
                className="mt-2 text-xs underline text-muted-foreground"
                onClick={() => setChoice(null)}
              >
                {t('common.cancel')}
              </button>
            </div>
          )}
          {miss && (
            <div className="mb-3 rounded border border-warning bg-warning/10 p-3 text-sm space-y-2">
              <div>
                {t('reception.noMatchAnywhere', {
                  text: miss.input,
                  mode: t(`reception.modes.${miss.tried}`),
                })}
              </div>
              {miss.next.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {miss.next.map((m) => (
                    <button
                      key={m}
                      type="button"
                      className="border rounded px-3 py-1 text-xs"
                      onClick={() => void scanItem(miss.input, m as Exclude<ScanMode, 'AUTO'>)}
                    >
                      {t('reception.tryMode', { mode: t(`reception.modes.${m}`) })}
                    </button>
                  ))}
                </div>
              )}
              <form
                className="flex gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (manualQuery.trim()) void scanItem(manualQuery.trim(), 'MANUAL');
                }}
              >
                <Input
                  value={manualQuery}
                  onChange={(e) => setManualQuery(e.target.value)}
                  placeholder={t('reception.manualSearch')}
                  className="flex-1"
                />
                <button type="submit" className="border rounded px-3 py-1 text-xs">
                  {t('reception.search')}
                </button>
              </form>
              <p className="text-xs text-muted-foreground">{t('reception.unknownItemHint')}</p>
            </div>
          )}

          <table className="w-full text-sm">
            <thead className="text-muted-foreground">
              <tr>
                <th className="text-left py-1">{t('common.product')}</th>
                <th className="text-left">{t('reception.destination')}</th>
                <th>{t('reception.ordered')}</th>
                <th>{t('reception.alreadyIn')}</th>
                <th>{t('reception.countedNow')}</th>
                <th>{t('reception.condition')}</th>
              </tr>
            </thead>
            <tbody>
              {po.lines.map((l) => {
                const c = counts[l.id] ?? { qty: 0, condition: 'OK' as Condition };
                const outstanding = l.quantity - l.quantityReceived;
                return (
                  <tr key={l.id} className="border-t">
                    <td className="py-1">
                      <div className="truncate max-w-[20rem]" title={l.productName ?? l.productId}>
                        {l.productName ?? l.productId}
                      </div>
                      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-2">
                        {l.productSku && <span className="font-mono">{l.productSku}</span>}
                        {l.productBrand && <span>{l.productBrand}</span>}
                        {l.ean && <span className="font-mono">EAN {l.ean}</span>}
                      </div>
                    </td>
                    <td className="text-xs">
                      {/* Step 10's split, announced at step 7: customer lines stay in Zone R
                          under their reservation, stock lines go to the racks. */}
                      {(() => {
                        const d = destinationOf(l.id);
                        if (!d) return <span className="text-muted-foreground">—</span>;
                        return d.destination === 'CUSTOMER_ORDER' ? (
                          <span className="inline-flex flex-col gap-0.5">
                            <Badge variant="warning" className="font-mono">
                              → {d.orderNumber ?? t('reception.customerOrder')}
                            </Badge>
                            {d.orderBin && (
                              <span className="font-mono text-[11px] text-muted-foreground">
                                {t('reception.intoOrderBin', { bin: d.orderBin })}
                              </span>
                            )}
                          </span>
                        ) : (
                          <Badge variant="secondary">{t('reception.toStock')}</Badge>
                        );
                      })()}
                    </td>
                    <td className="text-center">
                      {l.quantity}
                      {l.quantityCustomer != null && l.quantityCustomer < l.quantity && (
                        <div className="text-xs text-muted-foreground">
                          {t('reception.ofWhich', {
                            customer: l.quantityCustomer,
                            stock: l.quantity - l.quantityCustomer,
                          })}
                        </div>
                      )}
                      {l.customerReleased && (
                        <div className="text-xs text-warning">
                          {t('reception.customerReleased')}
                        </div>
                      )}
                    </td>
                    <td className="text-center">
                      {l.quantityReceived}
                      {outstanding > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {t('reception.due', { count: outstanding })}
                        </span>
                      )}
                    </td>
                    <td className="text-center">
                      {/* Touch targets sized for a warehouse tablet. */}
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => bump(l.id, -1)}
                          className="border rounded w-9 h-9 text-lg"
                          aria-label={t('reception.decrease', {
                            line: l.productName ?? t('reception.line'),
                          })}
                        >
                          −
                        </button>
                        <Input
                          type="number"
                          min={0}
                          value={c.qty}
                          onChange={(e) =>
                            setCounts((cur) => ({
                              ...cur,
                              [l.id]: {
                                qty: Math.max(0, Number(e.target.value) || 0),
                                condition: cur[l.id]?.condition ?? 'OK',
                              },
                            }))
                          }
                          className="h-9 w-16 px-1 text-center"
                          aria-label={t('reception.quantityReceivedFor', {
                            line: l.productName ?? t('reception.line'),
                          })}
                        />
                        <button
                          onClick={() => bump(l.id, 1)}
                          className="border rounded w-9 h-9 text-lg"
                          aria-label={t('reception.increase', {
                            line: l.productName ?? t('reception.line'),
                          })}
                        >
                          +
                        </button>
                      </div>
                      {/* How the last unit on this line was recognised (J2.5 legend). Absent on a
                          count typed by hand — the trail only records scans. */}
                      {identified[l.id] && (
                        <div
                          className="mt-1 text-xs"
                          title={t(`reception.confidence.${identified[l.id]}`)}
                        >
                          {identified[l.id] === 'GREEN'
                            ? '🟢'
                            : identified[l.id] === 'AMBER'
                              ? '🟡'
                              : '🔴'}{' '}
                          {t(`reception.confidence.${identified[l.id]}`)}
                        </div>
                      )}
                    </td>
                    <td className="text-center">
                      <Select
                        value={c.condition}
                        onChange={(e) =>
                          setCounts((cur) => ({
                            ...cur,
                            [l.id]: {
                              qty: cur[l.id]?.qty ?? 0,
                              condition: e.target.value as Condition,
                            },
                          }))
                        }
                        className="h-9 w-auto px-2"
                        aria-label={t('reception.conditionFor', {
                          line: l.productName ?? t('reception.line'),
                        })}
                      >
                        {(['OK', 'DAMAGED', 'MISSING'] as Condition[]).map((cond) => (
                          <option key={cond} value={cond}>
                            {t(`reception.conditions.${cond}`)}
                          </option>
                        ))}
                      </Select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {/* THE RECEPTION'S AUDIT TRAIL — every scan, and the employee who made it. The server has
          always recorded and returned this; the screen used to show only how many there were,
          which is the one thing nobody needs to know afterwards. */}
      {po && scanCount > 0 && (
        <section className="border rounded p-4 bg-card space-y-2">
          <h2 className="font-semibold text-sm">{t('reception.scanLog', { count: scanCount })}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground">
                <tr className="text-left">
                  <th className="py-1 pr-3 font-medium">{t('reception.log.when')}</th>
                  <th className="py-1 pr-3 font-medium">{t('reception.log.who')}</th>
                  <th className="py-1 pr-3 font-medium">{t('reception.log.part')}</th>
                  <th className="py-1 pr-3 font-medium">{t('reception.log.scanned')}</th>
                  <th className="py-1 pr-3 font-medium text-right">{t('reception.log.qty')}</th>
                  <th className="py-1 font-medium">{t('reception.log.condition')}</th>
                </tr>
              </thead>
              <tbody>
                {scanLog.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="py-1 pr-3 whitespace-nowrap tabular-nums">
                      {new Date(r.createdAt).toLocaleString()}
                    </td>
                    {/* A scan from before actor_id was recorded, or by a deleted account, has no
                        name — say so rather than leaving the cell blank. */}
                    <td className="py-1 pr-3 whitespace-nowrap">
                      {r.actorName ?? (
                        <span className="text-muted-foreground">
                          {t('reception.log.unknownWho')}
                        </span>
                      )}
                    </td>
                    <td className="py-1 pr-3">
                      {r.productSku ? (
                        <>
                          <span className="font-mono">{r.productSku}</span>
                          {r.productName ? (
                            <span className="text-muted-foreground"> · {r.productName}</span>
                          ) : null}
                        </>
                      ) : (
                        <span className="text-muted-foreground">{t('reception.log.noMatch')}</span>
                      )}
                    </td>
                    <td className="py-1 pr-3 font-mono text-muted-foreground">
                      {r.scanInput}
                      <span className="ml-1 not-italic">({r.scanMode})</span>
                    </td>
                    <td className="py-1 pr-3 text-right tabular-nums">{r.quantity}</td>
                    <td className="py-1">{r.condition}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      <button
        onClick={validate}
        disabled={busy || !po || toPost.length === 0}
        className="bg-success text-primary-foreground rounded px-4 py-2 disabled:opacity-40"
      >
        {t('reception.validate')}
        {toPost.length > 0 ? t('reception.validateCount', { count: toPost.length }) : ''}
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════════════════
 * E6 — GOODS RECEIPT IN ONE PASS
 * ══════════════════════════════════════════════════════════════════════════════════════════ */

interface ExpectedSku {
  productId: string;
  sku: string;
  name: string;
  brand: string | null;
  ean: string | null;
  customerUnits: number;
  replenishmentUnits: number;
  rackLocation: string | null;
  customerLines: Array<{
    orderId: string;
    orderNumber: string;
    customerName: string | null;
    promisedAt: string | null;
    units: number;
    alreadyReceived: number;
    bin: string | null;
    supplierName: string | null;
  }>;
  replenishmentLines: Array<{
    poNumber: string;
    supplierName: string | null;
    units: number;
    expectedAt: string | null;
  }>;
}

interface Placement {
  destination: 'CUSTOMER_ORDER' | 'REPLENISHMENT' | 'QUARANTINE';
  quantity: number;
  label: string | null;
  orderNumber?: string | null;
  customerName?: string | null;
  lineComplete?: boolean;
}

interface ScanOutcome {
  status: 'PLACED' | 'AMBIGUOUS' | 'NOT_IDENTIFIED';
  sessionId: string;
  mode: string;
  confidence: Confidence;
  productId: string | null;
  candidates: Array<{ productId: string; sku: string; name: string; brand: string | null }>;
  placements: Placement[];
}

interface Session {
  id: string;
  businessDay: string;
  openedAt: string;
  scanCount: number;
  missCount: number;
}

interface CallListRow {
  id: string;
  quantityShort: number;
  orderNumber: string | null;
  customerName: string | null;
  customerPhone: string | null;
  sku: string;
  productName: string;
  supplierName: string | null;
  newPromisedAt: string | null;
}

const frDate = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleDateString('fr-FR', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
      })
    : '—';

/**
 * The floor's day, exactly as guide E6 writes it.
 *
 *   "Scans each part as it comes out of the boxes. NO SUPPLIER TO CHOOSE, nothing typed or
 *    scanned from supplier paperwork: the delivery notes (often one per line) and the morning
 *    e-mails go to accounts."
 *
 * So there is no purchase-order picker on this screen, on purpose. There is a list of what is
 * expected today per SKU across every supplier, a scan box, and a button that closes the day.
 * Every decision — which customer a unit serves, which rack it goes to, whether it belongs in
 * Zone Q — is the SERVER's (INV-05/06: the screen never calculates); the page only shows where
 * the part was sent.
 */
function OnePass() {
  const { t } = useTranslation();
  const beep = useAudioFeedback();
  const { showApiError, push } = useToast();
  const [session, setSession] = useState<Session | null>(null);
  const [expected, setExpected] = useState<ExpectedSku[]>([]);
  const [placed, setPlaced] = useState<Array<Placement & { at: number; sku?: string }>>([]);
  const [choice, setChoice] = useState<{
    input: string;
    candidates: ScanOutcome['candidates'];
  } | null>(null);
  const [callList, setCallList] = useState<CallListRow[] | null>(null);
  const [brNumber, setBrNumber] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const [{ data: exp }, { data: sess }] = await Promise.all([
        apiClient.get<ExpectedSku[]>('/warehouse/receipts/expected'),
        apiClient.get<Session | null>('/warehouse/receipts/open'),
      ]);
      setExpected(exp);
      setSession(sess);
    } catch (err) {
      showApiError(err);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** One scan, or one counted quantity for a bulk replenishment. */
  const scan = async (
    input: string,
    qty?: number,
    productId?: string,
    condition?: 'OK' | 'DAMAGED'
  ) => {
    const value = input.trim();
    if (!value || busy) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post<ScanOutcome>(
        '/warehouse/receipts/scan',
        {
          scanInput: value,
          ...(qty ? { qty } : {}),
          ...(productId ? { productId } : {}),
          ...(condition ? { condition } : {}),
        },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      if (data.status === 'AMBIGUOUS') {
        beep.error();
        setChoice({ input: value, candidates: data.candidates });
        return;
      }
      if (data.status === 'NOT_IDENTIFIED') {
        beep.wrongProduct();
        push({ tone: 'warning', message: t('reception.onePass.notIdentified', { code: value }) });
        return;
      }
      beep.success();
      setChoice(null);
      setText('');
      setPlaced((cur) =>
        [...data.placements.map((pl) => ({ ...pl, at: Date.now() })), ...cur].slice(0, 40)
      );
      await refresh();
    } catch (err) {
      beep.error();
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  // The scanner talks to the page, not to a field: the operator has both hands in a box.
  useScanInput({ onScan: (code) => void scan(code) });

  /**
   * "When the day's deliveries have all arrived, the operative closes the day's receipts:
   *  whatever was expected today and has not been scanned is the discrepancy." The confirmation
   *  is deliberate — closing early is what produces a false alarm while a second van is still on
   *  its way, which is the one thing E6 says must not happen.
   */
  const closeDay = async () => {
    if (!window.confirm(t('reception.onePass.confirmClose'))) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post<{ brNumber: string; callList: CallListRow[] }>(
        '/warehouse/receipts/close',
        {},
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      setCallList(data.callList);
      setBrNumber(data.brNumber);
      push({
        tone: data.callList.length > 0 ? 'warning' : 'success',
        message:
          data.callList.length > 0
            ? t('reception.onePass.closedWithCalls', {
                br: data.brNumber,
                count: data.callList.length,
              })
            : t('reception.onePass.closedClean', { br: data.brNumber }),
      });
      await refresh();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const totalCustomer = expected.reduce((n, e) => n + e.customerUnits, 0);
  const totalStock = expected.reduce((n, e) => n + e.replenishmentUnits, 0);

  return (
    <div className="space-y-4">
      <section className="border rounded p-4 bg-card space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-semibold">{t('reception.onePass.scanTitle')}</h2>
          <div className="text-xs text-muted-foreground">
            {session
              ? t('reception.onePass.dayOpen', {
                  day: session.businessDay,
                  scans: session.scanCount,
                  misses: session.missCount,
                })
              : t('reception.onePass.dayNotOpen')}
          </div>
        </div>
        <form
          className="flex flex-wrap gap-2 items-center"
          onSubmit={(e) => {
            e.preventDefault();
            void scan(text);
          }}
        >
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('reception.onePass.scanPlaceholder')}
            aria-label={t('reception.onePass.scanTitle')}
            className="flex-1 min-w-[18rem] font-mono"
            autoFocus
          />
          <button
            type="submit"
            disabled={busy || !text.trim()}
            className="bg-primary text-primary-foreground rounded px-4 py-2 disabled:opacity-40"
          >
            {t('reception.onePass.scanAction')}
          </button>
          <button
            type="button"
            disabled={busy || !text.trim()}
            onClick={() => void scan(text, 1, undefined, 'DAMAGED')}
            className="border rounded px-4 py-2 disabled:opacity-40"
          >
            {t('reception.onePass.damaged')}
          </button>
        </form>
        <p className="text-xs text-muted-foreground">{t('reception.onePass.scanHint')}</p>
      </section>

      {/* Ambiguity is the operator's to resolve, by eye — never the server's to guess. */}
      {choice && (
        <section className="border rounded p-4 bg-warning/10 space-y-2">
          <p className="text-sm font-medium">
            {t('reception.pickCandidate', { text: choice.input })}
          </p>
          <ul className="space-y-1">
            {choice.candidates.map((c) => (
              <li key={c.productId} className="flex items-center justify-between gap-2 text-sm">
                <span>
                  <span className="font-mono">{c.sku}</span> — {c.name}
                  {c.brand ? ` · ${c.brand}` : ''}
                </span>
                <button
                  className="border rounded px-2 py-1 text-xs"
                  onClick={() => void scan(choice.input, 1, c.productId)}
                >
                  {t('reception.thisOne')}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* "Before scanning, the receiving screen shows the exact number of units expected today
          per SKU, all suppliers together, the supplier being shown as information only." */}
      <section className="border rounded p-4 bg-card">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
          <h2 className="font-semibold">{t('reception.onePass.expectedTitle')}</h2>
          <span className="text-xs text-muted-foreground">
            {t('reception.onePass.expectedTotals', { customer: totalCustomer, stock: totalStock })}
          </span>
        </div>
        {expected.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('reception.onePass.nothingExpected')}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr className="text-left">
                <th>{t('reception.onePass.part')}</th>
                <th className="text-center">{t('reception.onePass.customerUnits')}</th>
                <th className="text-center">{t('reception.onePass.stockUnits')}</th>
                <th>{t('reception.onePass.detail')}</th>
                <th className="text-right">{t('reception.onePass.countIn')}</th>
              </tr>
            </thead>
            <tbody>
              {expected.map((e) => (
                <tr key={e.productId} className="border-t align-top">
                  <td className="py-2">
                    <div className="font-medium">{e.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {e.sku}
                      {e.ean ? ` · ${e.ean}` : ''}
                    </div>
                  </td>
                  <td className="text-center font-semibold">{e.customerUnits || '—'}</td>
                  <td className="text-center">{e.replenishmentUnits || '—'}</td>
                  <td className="text-xs space-y-0.5">
                    {e.customerLines.map((l) => (
                      <div key={l.orderId}>
                        <Badge variant="secondary" className="mr-1">
                          {l.units}
                        </Badge>
                        {l.customerName ?? '—'} · {l.orderNumber} ·{' '}
                        {t('reception.onePass.promised', { date: frDate(l.promisedAt) })}
                        {l.bin ? ` · ${t('reception.intoOrderBin', { bin: l.bin })}` : ''}
                        {/* The supplier is a label, never a key (E6 "Mixed deliveries"). */}
                        {l.supplierName ? (
                          <span className="text-muted-foreground"> · {l.supplierName}</span>
                        ) : null}
                      </div>
                    ))}
                    {e.replenishmentLines.map((l, i) => (
                      <div key={`${l.poNumber}-${i}`} className="text-muted-foreground">
                        {l.units} · {t('reception.onePass.toRack', { rack: e.rackLocation ?? '—' })}{' '}
                        · {l.poNumber}
                        {l.supplierName ? ` · ${l.supplierName}` : ''}
                      </div>
                    ))}
                  </td>
                  <td className="text-right">
                    {/* "For replenishment of many units of the same SKU, the operator enters a
                        counted quantity instead of scanning each unit; customer units are still
                        served first, in promised-date order, and the rest goes to the location." */}
                    <CountIn
                      disabled={busy || !e.ean}
                      onCount={(n) => void scan(e.ean ?? e.sku, n)}
                      label={t('reception.onePass.countIn')}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {placed.length > 0 && (
        <section className="border rounded p-4 bg-card">
          <h2 className="font-semibold mb-2">{t('reception.onePass.placedTitle')}</h2>
          <ul className="text-sm space-y-1">
            {placed.map((p, i) => (
              <li key={`${p.at}-${i}`} className="flex items-center gap-2">
                <Badge
                  variant={
                    p.destination === 'CUSTOMER_ORDER'
                      ? 'success'
                      : p.destination === 'REPLENISHMENT'
                        ? 'secondary'
                        : 'warning'
                  }
                >
                  {p.quantity}
                </Badge>
                {p.destination === 'CUSTOMER_ORDER'
                  ? t('reception.onePass.toBin', {
                      bin: p.label ?? '—',
                      customer: p.customerName ?? '—',
                      order: p.orderNumber ?? '',
                    })
                  : p.destination === 'REPLENISHMENT'
                    ? t('reception.onePass.toRack', { rack: p.label ?? '—' })
                    : t('reception.onePass.toQuarantine')}
                {p.destination === 'CUSTOMER_ORDER' && p.lineComplete === false && (
                  <span className="text-xs text-muted-foreground">
                    {t('reception.onePass.partialLine')}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <button
          onClick={() => void closeDay()}
          disabled={busy || !session}
          className="bg-success text-primary-foreground rounded px-4 py-2 disabled:opacity-40"
        >
          {t('reception.onePass.closeDay')}
        </button>
        <span className="text-xs text-muted-foreground">{t('reception.onePass.closeHint')}</span>
      </div>

      {/* "Closing the day's receipts produces the call list: one line per shortage, with the
          customer, the part, the supplier it was expected from, the new date and the order.
          Calls are made straight away, before the afternoon pickups." */}
      {callList && (
        <section className="border rounded p-4 bg-card">
          <h2 className="font-semibold mb-2">
            {t('reception.onePass.callListTitle', { br: brNumber ?? '' })}
          </h2>
          {callList.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t('reception.onePass.nothingShort')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground text-left">
                <tr>
                  <th>{t('reception.onePass.customer')}</th>
                  <th>{t('reception.onePass.part')}</th>
                  <th className="text-center">{t('reception.onePass.qty')}</th>
                  <th>{t('reception.onePass.supplier')}</th>
                  <th>{t('reception.onePass.newDate')}</th>
                  <th>{t('reception.onePass.order')}</th>
                </tr>
              </thead>
              <tbody>
                {callList.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="py-1">
                      {r.customerName ?? t('reception.onePass.replenishmentOnly')}
                      {r.customerPhone ? (
                        <span className="text-xs text-muted-foreground"> · {r.customerPhone}</span>
                      ) : null}
                    </td>
                    <td>
                      {r.productName}
                      <span className="text-xs text-muted-foreground font-mono"> {r.sku}</span>
                    </td>
                    <td className="text-center">{r.quantityShort}</td>
                    <td className="text-xs">{r.supplierName ?? '—'}</td>
                    <td>{frDate(r.newPromisedAt)}</td>
                    <td className="font-mono text-xs">{r.orderNumber ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}

/** The bulk count for replenishment: a number and a button, nothing clever. */
function CountIn({
  disabled,
  onCount,
  label,
}: {
  disabled: boolean;
  onCount: (n: number) => void;
  label: string;
}) {
  const [n, setN] = useState('');
  return (
    <span className="inline-flex gap-1">
      <Input
        type="number"
        min={1}
        value={n}
        onChange={(e) => setN(e.target.value)}
        className="w-20 h-9"
        aria-label={label}
        disabled={disabled}
      />
      <button
        type="button"
        className="border rounded px-2 py-1 text-xs disabled:opacity-40"
        disabled={disabled || !(Number(n) > 0)}
        onClick={() => {
          onCount(Number(n));
          setN('');
        }}
      >
        {label}
      </button>
    </span>
  );
}

/**
 * Screen 30 — the two receptions, side by side.
 *
 * ONE PASS is the guide's (E6) and the default: the operator scans parts out of boxes and the
 * system decides where each one goes, with no supplier chosen. BY PURCHASE ORDER is the buyer's
 * per-PO close, which still owns §8c short-ship and the credit release, and is the way in when
 * someone is reconciling one specific delivery against one specific order.
 */
/** Which tab this operator used last. The DEFAULT stays the one-pass receipt (owner decision,
 *  2026-09-20: it is the flow the guide describes, and a default that contradicts the written
 *  process teaches the wrong habit) — but somebody who deliberately works purchase-order first
 *  should not have to re-choose every morning. Per-browser, never shared. */
const TAB_KEY = 'mecadepot.reception.tab';

function rememberedTab(): 'ONE_PASS' | 'BY_PO' {
  try {
    return localStorage.getItem(TAB_KEY) === 'BY_PO' ? 'BY_PO' : 'ONE_PASS';
  } catch {
    return 'ONE_PASS';
  }
}

export default function Reception() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'ONE_PASS' | 'BY_PO'>(rememberedTab);
  const choose = (k: 'ONE_PASS' | 'BY_PO') => {
    setTab(k);
    try {
      localStorage.setItem(TAB_KEY, k);
    } catch {
      /* private window, blocked storage — the tab still switches, it just is not remembered. */
    }
  };
  return (
    <div className="space-y-4">
      <PageHeader title={t('reception.title')} />
      <div className="flex gap-2 border-b">
        {(['ONE_PASS', 'BY_PO'] as const).map((k) => (
          <button
            key={k}
            onClick={() => choose(k)}
            className={`px-3 py-2 text-sm -mb-px border-b-2 ${
              tab === k
                ? 'border-primary font-semibold'
                : 'border-transparent text-muted-foreground'
            }`}
          >
            {t(`reception.tabs.${k}`)}
          </button>
        ))}
      </div>
      {tab === 'ONE_PASS' ? <OnePass /> : <ByPurchaseOrder />}
    </div>
  );
}
